import { Link } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import {
  isSessionActive,
  listAllocations,
  listAttendanceRecords,
  listAttendanceSessions,
  listEnrollments,
  listNotifications,
  listSubmissions,
} from '@/lib/api'
import { useAsyncData } from '@/lib/useAsyncData'

export function LecturerDashboard() {
  const { user } = useAuth()
  const allocations = useAsyncData(listAllocations, [])
  const sessions = useAsyncData(listAttendanceSessions, [])
  const records = useAsyncData(listAttendanceRecords, [])
  const submissions = useAsyncData(listSubmissions, [])
  const enrollments = useAsyncData(listEnrollments, [])
  const notifications = useAsyncData(listNotifications, [])

  const courseIds = new Set((allocations.data || []).map((a) => a.course))
  const studentCount = new Set(
    (enrollments.data || []).filter((e) => courseIds.has(e.course)).map((e) => e.student),
  ).size
  const sessionCount = (sessions.data || []).length
  const presentCount = (records.data || []).filter((r) => r.status === 'PRESENT').length
  const avgAttendance =
    sessionCount === 0 ? '—' : `${Math.round((presentCount / Math.max(sessionCount * Math.max(studentCount, 1), 1)) * 100)}%`
  const pendingMarking = (submissions.data || []).filter((s) => s.marks_obtained == null).length
  const active = (sessions.data || []).find((s) => isSessionActive(s))
  const featured = (allocations.data || [])[0]
  const unread = (notifications.data || []).filter((n) => !n.is_read).length
  const loading = allocations.loading || sessions.loading

  return (
    <div className="space-y-6 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Lecturer Dashboard</h1>
          <p className="text-sm text-slate-500">
            Welcome, {user?.first_name || user?.username}
            {featured ? ` · ${featured.semester_name}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button type="button" className="relative rounded-full bg-white p-2 text-slate-500 shadow-sm hover:text-slate-700">
            <i className="fa-regular fa-bell text-lg" />
            {unread > 0 && (
              <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500" />
            )}
          </button>
          <Link
            to="/lecturer/attendance"
            className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-emerald-700"
          >
            <i className="fa-solid fa-plus" /> Open Attendance
          </Link>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading dashboard…</p>}
      {allocations.error && (
        <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{allocations.error}</p>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <Metric label="My Courses" value={String((allocations.data || []).length)} icon="fa-book-bookmark" tone="bg-blue-50 text-blue-500" />
        <Metric label="Total Students" value={String(studentCount)} icon="fa-users" tone="bg-emerald-50 text-emerald-500" />
        <Metric label="Avg. Attendance" value={avgAttendance} icon="fa-user-check" tone="bg-indigo-50 text-indigo-500" />
        <Metric label="Pending Marking" value={String(pendingMarking)} icon="fa-clock" tone="bg-amber-50 text-amber-500" regular />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="space-y-5 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm lg:col-span-2">
          {featured ? (
            <>
              <div className="flex items-center justify-between">
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-bold text-emerald-700 uppercase">
                  {active ? 'Session open' : 'Ready'}
                </span>
                <span className="text-xs text-slate-400">{studentCount} enrolled (your courses)</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  {featured.course_details.title} ({featured.course_details.code})
                </h2>
                <p className="mt-1 text-xs text-slate-500">{featured.semester_name}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/lecturer/attendance"
                  className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-xs font-semibold text-white hover:bg-emerald-700"
                >
                  <i className="fa-solid fa-qrcode" /> Open attendance
                </Link>
                <Link
                  to="/lecturer/materials"
                  className="flex items-center gap-2 rounded-xl bg-slate-800 px-5 py-3 text-xs font-semibold text-white hover:bg-slate-900"
                >
                  <i className="fa-solid fa-folder-open" /> Materials
                </Link>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3.5 text-xs text-slate-600">
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-info text-blue-500" /> Attendance status:{' '}
                  <strong>{active ? `${active.course_code} open` : 'Not started'}</strong>
                </span>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500">No course allocations yet. Ask an admin to allocate courses.</p>
          )}
        </section>

        <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <h3 className="font-bold text-slate-800">Notifications</h3>
          {(notifications.data || []).slice(0, 3).map((n) => (
            <div key={n.id} className="rounded-xl border border-slate-100 p-4">
              <p className="text-sm font-semibold text-slate-800">{n.title}</p>
              <p className="mt-1 text-xs text-slate-500">{n.message}</p>
            </div>
          ))}
          {(notifications.data || []).length === 0 && (
            <p className="text-sm text-slate-500">No notifications.</p>
          )}
        </section>
      </div>
    </div>
  )
}

function Metric({
  label,
  value,
  icon,
  tone,
  regular,
}: {
  label: string
  value: string
  icon: string
  tone: string
  regular?: boolean
}) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
      <div className="space-y-1">
        <span className="text-xs font-bold tracking-wider text-slate-400 uppercase">{label}</span>
        <p className="text-3xl font-extrabold text-slate-800">{value}</p>
      </div>
      <div className={`flex h-12 w-12 items-center justify-center rounded-2xl text-xl ${tone}`}>
        <i className={`${regular ? 'fa-regular' : 'fa-solid'} ${icon}`} />
      </div>
    </div>
  )
}
